import React, { useContext, useEffect, useState } from 'react';
import {
	IStyleSettingsImageUploaderContext,
	StyleSettingsImageUploaderContext,
} from '../styleSettingsImageUploaderState/styleSettingsImageUploaderContext';
import { useBlobDownload } from 'hooks/useBlobDownload';
import { getFilePathForMediaFilesWithSizeForStyleSettings } from 'backend/additionalInformation';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import Text from 'components/Text/Text';

const imageMaxHeight = 384;

interface IProps {
	filePath: Nullable<string>;
	translationId: string;
	itemKey: Nullable<string>;
	isNeedImgHeading?: boolean;
	groupLoading: boolean;
	isMakeImageRequest: boolean;
	isDisabledControls?: boolean;
	deleteFromNormalDisplayHandler: () => void;
}

export const UploaderNew: React.FC<IProps> = ({
	filePath,
	translationId,
	itemKey,
	isNeedImgHeading = false,
	groupLoading,
	isMakeImageRequest,
	isDisabledControls,
	deleteFromNormalDisplayHandler,
}) => {
	const {
		changeTypeOfModalWindow,
		changeTypeOfErrorModalWindow,
		setCropperSrc,
		setUploaderSrc,
		setLoadingImageError,
		setLoading,
		uploaderSrc,
		loadingImageError,
		isLoading,
		isNewImageUploaded,
	} = useContext<IStyleSettingsImageUploaderContext>(StyleSettingsImageUploaderContext);
	const [isShowImage, setIsShowImage] = useState<boolean>(false);
	const [imageRatio, setImageRatio] = useState<Nullable<number>>(null);
	const imageWidth = imageRatio ? `min(100%, ${Math.floor(imageMaxHeight * imageRatio)}px)` : '100%';
	const downloadBlob = useBlobDownload({
		filePath,
		fileExtension: filePath?.split('.').pop() === 'gif' ? 'gif' : 'png',
		fileName: 'styleImage',
		onError: () => changeTypeOfErrorModalWindow('fileDownloadError'),
	});

	useEffect(() => {
		if (!isShowImage && isMakeImageRequest) {
			setIsShowImage(true);
		}
	}, [isMakeImageRequest]);

	useEffect(() => {
		const getImagePath = async () => {
			setLoading(true);
			const response = await getFilePathForMediaFilesWithSizeForStyleSettings(translationId, 'Medium');
			setUploaderSrc(response.data.preSignedUrl);
			setLoading(false);
		};

		isShowImage && translationId && filePath && getImagePath();
	}, [isShowImage]);

	const openCropper = () => {
		changeTypeOfModalWindow('cropper');
	};

	const deletePhoto = () => {
		setUploaderSrc(null);
		setCropperSrc(null);
		setLoadingImageError(false);
		deleteFromNormalDisplayHandler();
	};

	const showErrorImage = () => {
		setLoadingImageError(true);
	};

	const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
		setImageRatio(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight);
	};

	const renderImage = () => {
		if (isLoading && !groupLoading) {
			return (
				<div className="relative h-40 rounded overflow-hidden">
					<LoaderWithProps loaderSize="big" />
				</div>
			);
		}

		if (loadingImageError) {
			return (
				<div className="h-40 rounded flex items-center justify-center bg-purple-01-new" data-test="ImageError">
					<Text translationNameSpace="form" translationKey="imageLoadError" theme="neutralMedium" size="xs" />
				</div>
			);
		}

		if (!uploaderSrc) {
			return (
				<div className="h-40 rounded flex items-center justify-center bg-purple-01-new">
					<Icon name={IconName.image} size="smallHero" className="text-purple-03-new" />
				</div>
			);
		}

		return (
			<div className="relative mx-auto" style={{ width: imageWidth }}>
				{filePath && !isNewImageUploaded && (
					<div
						className="absolute inset-0 flex items-center justify-center bg-grey-10-new opacity-0 cursor-pointer transition-opacity hover:opacity-60"
						data-test="DownloadImage"
						onClick={downloadBlob}>
						<Icon name={IconName.download} size="xxxl" className="text-white-00-new" />
					</div>
				)}
				<img
					className="w-full max-h-96 block object-contain"
					src={uploaderSrc}
					alt="Photo"
					onLoad={handleImageLoad}
					onError={showErrorImage}
				/>
			</div>
		);
	};

	return (
		<div className="min-w-0 flex-1 flex flex-col gap-4" data-test="ImageUploader">
			<div className="flex flex-col gap-2">
				{isNeedImgHeading && (
					<Text translationNameSpace="mediaSettings" translationKey={itemKey} size="ml" weight="bold" />
				)}
				{renderImage()}
			</div>
			<div className="flex gap-4">
				<NewButton
					size="small"
					iconLeft={<Icon name={uploaderSrc ? IconName.refresh : IconName.upload} size="lg" />}
					disabled={isDisabledControls}
					testId="uploadReloadButton"
					onClick={openCropper}>
					<Text
						translationNameSpace="form"
						translationKey={uploaderSrc ? 'replaceButton' : 'uploadButton'}
						asFragment={true}
					/>
				</NewButton>
				{uploaderSrc && (
					<NewButton
						variant="outline"
						color="red"
						size="small"
						iconLeft={<Icon name={IconName.delete} size="lg" />}
						disabled={isDisabledControls}
						testId="deleteButton"
						onClick={deletePhoto}>
						<Text translationNameSpace="form" translationKey="deleteButton" asFragment={true} />
					</NewButton>
				)}
			</div>
		</div>
	);
};
